import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, LockKeyhole, Mail } from "lucide-react";
import { papi, errText } from "./portalApi";

const inputCls =
  "w-full rounded-lg border border-white/15 bg-navy-900/60 px-4 py-3 text-sm text-ivory placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";

export default function PortalLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("login"); // login | change | forgot
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await papi.post("/login", { email, password });
      localStorage.setItem("rcs_portal_token", r.data.token);
      if (r.data.mustChangePassword) {
        setStage("change");
        toast.info("First sign-in detected — please set your own password.");
      } else {
        toast.success(`Welcome back, ${r.data.employee.fullName.split(" ")[0]}.`);
        onSuccess();
      }
    } catch (err) {
      toast.error(errText(err, "Sign in failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await papi.post("/change-password", { currentPassword: password, newPassword });
      toast.success("Password set successfully. Welcome to the portal.");
      onSuccess();
    } catch (err) {
      toast.error(errText(err, "Could not update your password."));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!email) return toast.error("Please enter your official email.");
    setLoading(true);
    try {
      await papi.post("/forgot-password", { email });
      setOtpSent(true);
      toast.success("If this email is registered, a verification code is on its way.");
    } catch (err) {
      toast.error(errText(err, "Could not send the code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await papi.post("/reset-password", { email, otp, newPassword });
      toast.success("Password reset. Please sign in with your new password.");
      setStage("login");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setOtpSent(false);
    } catch (err) {
      toast.error(errText(err, "Could not reset the password."));
    } finally {
      setLoading(false);
    }
  };

  const subtitles = {
    login: "Sign in to your workspace",
    change: "Set your new password",
    forgot: "Password recovery",
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-950 px-6" data-testid="portal-login-page">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-gold/20 bg-navy-900/60 p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="flex flex-col items-center text-center">
            <img src="/rcs-logo.png" alt="Roy Career Solutions Private Limited" className="h-16 w-auto" />
            <h1 className="mt-6 font-serif text-2xl text-ivory">Employee Portal</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-slate-500">{subtitles[stage]}</p>
          </div>

          {stage === "login" && (
            <form onSubmit={login} className="mt-8 space-y-4" data-testid="portal-login-form">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="Official email"
                  aria-label="Official email"
                  className={`${inputCls} pl-11`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="portal-login-email"
                />
              </div>
              <div className="relative">
                <LockKeyhole className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  placeholder="Password"
                  aria-label="Password"
                  className={`${inputCls} pl-11 pr-11`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="portal-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-gold"
                  data-testid="portal-login-toggle-password"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="portal-login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </button>
              <button
                type="button"
                onClick={() => setStage("forgot")}
                className="w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                data-testid="portal-forgot-link"
              >
                Forgot password?
              </button>
              <p className="text-center text-[11px] leading-relaxed text-slate-500">
                First-time users sign in with the temporary password shared by HR.
              </p>
            </form>
          )}

          {stage === "change" && (
            <form onSubmit={changePassword} className="mt-8 space-y-4" data-testid="portal-change-password-form">
              <div className="rounded-lg border border-gold/25 bg-gold/5 px-4 py-3 text-xs leading-relaxed text-gold-light">
                For security, replace your temporary password with one only you know (minimum 8 characters).
              </div>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="New password"
                  aria-label="New password"
                  className={`${inputCls} pl-11`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="portal-new-password"
                />
              </div>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  aria-label="Confirm new password"
                  className={`${inputCls} pl-11`}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="portal-confirm-password"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="portal-change-submit">
                {loading ? "Updating…" : "Set Password & Continue"}
              </button>
            </form>
          )}

          {stage === "forgot" && (
            <div className="mt-8 space-y-4" data-testid="portal-forgot-form">
              <p className="text-center text-xs leading-relaxed text-slate-400">
                Enter your official RCS email. We will send a 6-digit verification code to it.
              </p>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="Official email"
                  aria-label="Official email"
                  className={`${inputCls} pl-11`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={otpSent}
                  data-testid="portal-forgot-email"
                />
              </div>
              {!otpSent ? (
                <button onClick={sendOtp} disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="portal-send-otp">
                  {loading ? "Sending…" : "Send Verification Code"}
                </button>
              ) : (
                <form onSubmit={resetPassword} className="space-y-4">
                  <input
                    required
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    aria-label="Verification code"
                    className={`${inputCls} text-center text-lg tracking-[0.5em]`}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    data-testid="portal-otp-input"
                  />
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="New password (min 8 characters)"
                    aria-label="New password"
                    className={inputCls}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="portal-reset-new-password"
                  />
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                    aria-label="Confirm new password"
                    className={inputCls}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="portal-reset-confirm-password"
                  />
                  <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="portal-reset-submit">
                    {loading ? "Updating…" : "Reset Password"}
                  </button>
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={loading}
                    className="w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                    data-testid="portal-resend-otp"
                  >
                    Resend code
                  </button>
                </form>
              )}
              <button
                type="button"
                onClick={() => { setStage("login"); setOtpSent(false); setOtp(""); }}
                className="w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                data-testid="portal-back-to-login"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>
        <p className="mt-6 text-center text-[11px] text-slate-600">
          Roy Career Solutions Private Limited · Authorised access only
        </p>
      </div>
    </div>
  );
}
